import type { MapEventTypes, EventHandler, EventUnsubscriber } from '../types/events';

/**
 * 类型安全的事件总线
 *
 * 层级：map-core（"地图怎么动"）
 *
 * 职责：
 * 1. 提供应用级事件发布/订阅能力
 * 2. 支持预定义的 MapEventTypes 事件名和自定义事件
 * 3. 统一处理 handler 异常，避免单个回调抛错打断其他监听者
 *
 * 不关心：事件来源是地图原生事件、覆盖物事件还是业务事件
 */
export class MapEventBus {
  private handlers = new Map<string, Set<EventHandler>>();

  /** 监听预定义事件 */
  on<K extends keyof MapEventTypes>(
    event: K,
    handler: EventHandler<MapEventTypes[K]>,
  ): EventUnsubscriber;
  /** 监听自定义事件 */
  on(event: string, handler: EventHandler): EventUnsubscriber;
  on(event: string, handler: EventHandler): EventUnsubscriber {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);
    return () => this.off(event, handler);
  }

  off(event: string, handler: EventHandler): void {
    this.handlers.get(event)?.delete(handler);
  }

  /** 触发预定义事件 */
  emit<K extends keyof MapEventTypes>(
    event: K,
    payload: MapEventTypes[K],
  ): void;
  /** 触发自定义事件 */
  emit(event: string, payload?: any): void;
  emit(event: string, payload?: any): void {
    this.handlers.get(event)?.forEach((handler) => {
      try {
        handler(payload);
      } catch (error) {
        console.error(
          `[MapEventBus] Error in handler for "${event}":`,
          error
        );
      }
    });
  }

  clear(): void {
    this.handlers.clear();
  }

  /** 获取某个事件的监听器数量（调试用） */
  listenerCount(event: string): number {
    return this.handlers.get(event)?.size ?? 0;
  }
}
